import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Form,
  InputGroup,
  Alert,
  ProgressBar,
} from 'react-bootstrap';
import {
  ArrowLeft,
  CheckCircle,
  CheckCircleFill,
  Clock,
  Calendar,
  Person,
  Envelope,
  Search,
  Filter,
  SortAlphaDown,
  Bell,
  BellFill,
  ArrowClockwise,
  Briefcase,
  GeoAlt,
  ListTask,
  CalendarCheck,
  CalendarEvent,
} from 'react-bootstrap-icons';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'http://34.206.193.218:4001/api/admin';

const ToDoList = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [projectInfo, setProjectInfo] = useState(null);
  
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

  /* ================= FETCH TODOS ================= */
  const fetchTodos = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/data/getTodosByProject/${projectId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setTodos(res.data.todos || []);
        
        // Extract project info from todos if available
        if (res.data.todos && res.data.todos.length > 0) {
          // Check if project info is embedded in todos
          const firstTodo = res.data.todos[0];
          if (firstTodo.projectId) {
            setProjectInfo({
              name: firstTodo.projectId.name || `Project ${projectId.substring(0, 8)}`,
              lotNo: firstTodo.projectId.lotNo || 'N/A',
              projectLocation: firstTodo.projectId.projectLocation || 'Not specified',
              createdAt: firstTodo.projectId.createdAt,
              _id: firstTodo.projectId._id || projectId
            });
          } else {
            // Use fallback project info
            setProjectInfo({
              name: `Project ${projectId.substring(0, 8)}`,
              lotNo: 'N/A',
              projectLocation: 'Not specified',
              createdAt: new Date().toISOString(),
              _id: projectId
            });
          }
        } else {
          // No todos, but we can still show basic project info
          setProjectInfo({
            name: `Project ${projectId.substring(0, 8)}`,
            lotNo: 'N/A',
            projectLocation: 'Not specified',
            createdAt: new Date().toISOString(),
            _id: projectId
          });
        }
      }
    } catch (error) {
      console.error('Fetch todos error:', error);
      toast.error('Failed to load todos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchTodos();
    }
  }, [projectId]);

  /* ================= CALCULATIONS ================= */
  const calculateStats = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const upcomingTodos = todos.filter(todo => {
      const taskDate = new Date(todo.taskDate);
      taskDate.setHours(0, 0, 0, 0);
      return taskDate >= today;
    }).length;
    
    const overdueTodos = todos.filter(todo => {
      const taskDate = new Date(todo.taskDate);
      taskDate.setHours(0, 0, 0, 0);
      return taskDate < today;
    }).length;
    
    const notificationTodos = todos.filter(todo => todo.toggleNotification).length;
    
    return {
      total: todos.length,
      upcoming: upcomingTodos,
      overdue: overdueTodos,
      withNotification: notificationTodos,
      upcomingPercentage: todos.length > 0 ? (upcomingTodos / todos.length) * 100 : 0,
    };
  };

  /* ================= FILTER & SORT ================= */
  const filteredTodos = todos
    .filter((todo) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const taskDate = new Date(todo.taskDate);
      taskDate.setHours(0, 0, 0, 0);
      
      if (filterStatus === 'upcoming' && taskDate < today) return false;
      if (filterStatus === 'overdue' && taskDate >= today) return false;
      if (filterStatus === 'notification' && !todo.toggleNotification) return false;
      
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          todo.title?.toLowerCase().includes(searchLower) ||
          todo.description?.toLowerCase().includes(searchLower) ||
          todo.userId?.name?.toLowerCase().includes(searchLower) ||
          todo.userId?.email?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'date':
          aVal = new Date(a.taskDate).getTime();
          bVal = new Date(b.taskDate).getTime();
          break;
        case 'title':
          aVal = a.title || '';
          bVal = b.title || '';
          break;
        case 'user':
          aVal = a.userId?.name || '';
          bVal = b.userId?.name || '';
          break;
        default:
          aVal = new Date(a.taskDate).getTime();
          bVal = new Date(b.taskDate).getTime();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  /* ================= FORMATTERS ================= */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDateStatus = (taskDate) => {
    if (!taskDate) return { label: 'No date', variant: 'secondary' };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(taskDate);
    date.setHours(0, 0, 0, 0);
    
    if (date.getTime() === today.getTime()) {
      return { label: 'Today', variant: 'warning' };
    } else if (date < today) {
      return { label: 'Overdue', variant: 'danger' };
    } else {
      const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
      return { label: `In ${diffDays} days`, variant: 'info' };
    }
  };

  const getInitials = (name) => {
    if (!name) return 'NN';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading todos...</span>
      </div>
    );
  }

  const stats = calculateStats();

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3 d-flex align-items-center"
              >
                <ArrowLeft className="me-2" />
                Back to Project List
              </Button>
              <h2 className="fw-bold mb-2">
                <ListTask className="me-3" />
                Project To-Do List
              </h2>
              <div className="d-flex align-items-center">
                <Badge bg="info" className="px-3 py-2 me-3">
                  <CheckCircle className="me-2" />
                  {todos.length} Task{todos.length !== 1 ? 's' : ''}
                </Badge>
                <div className="d-flex align-items-center">
                  <Badge bg="primary" className="me-2">
                    <Briefcase className="me-1" />
                    {projectInfo?.name || `Project ${projectId.substring(0, 8)}...`}
                  </Badge>
                  <Badge bg="secondary">
                    <GeoAlt className="me-1" />
                    Lot: {projectInfo?.lotNo || 'N/A'}
                  </Badge>
                </div>
              </div>
            </div>
            
            <div>
              <Button
                variant="primary"
                onClick={fetchTodos}
                className="d-flex align-items-center"
              >
                <ArrowClockwise className="me-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* PROJECT INFO */}
          <Card className="mb-4 border-0 shadow-sm bg-light">
            <Card.Body>
              <Row>
                <Col md={8}>
                  <div className="d-flex align-items-center mb-2">
                    <Briefcase className="me-3 text-primary" />
                    <div>
                      <h6 className="mb-0">Project: {projectInfo?.name || `Project ${projectId.substring(0, 8)}...`}</h6>
                      <small className="text-muted">
                        Location: {projectInfo?.projectLocation || 'Not specified'}
                      </small>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="d-flex align-items-center mb-2">
                    <Calendar className="me-3 text-primary" />
                    <div>
                      <h6 className="mb-0">Project ID: {projectId.substring(0, 8)}...</h6>
                      <small className="text-muted">
                        {projectInfo?.createdAt ? `Created: ${formatDate(projectInfo.createdAt)}` : 'Project details'}
                      </small>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* STATISTICS */}
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="text-center">
                  <div className="mb-2">
                    <ListTask size={24} className="text-primary" />
                  </div>
                  <div className="fw-bold display-6">{stats.total}</div>
                  <small className="text-muted">Total Tasks</small>
                </Card.Body>
              </Card>
            </Col>
            
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="text-center">
                  <div className="mb-2">
                    <CalendarEvent size={24} className="text-info" />
                  </div>
                  <div className="fw-bold fs-4">{stats.upcoming}</div>
                  <small className="text-muted">Upcoming</small>
                </Card.Body>
              </Card>
            </Col>
            
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="text-center">
                  <div className="mb-2">
                    <CalendarCheck size={24} className="text-warning" />
                  </div>
                  <div className="fw-bold fs-4">{stats.overdue}</div>
                  <small className="text-muted">Overdue</small>
                </Card.Body>
              </Card>
            </Col>
            
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="text-center">
                  <div className="mb-2">
                    <Bell size={24} className="text-success" />
                  </div>
                  <div className="fw-bold fs-4">{stats.withNotification}</div>
                  <small className="text-muted">With Notification</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* UPCOMING PROGRESS */}
          {todos.length > 0 && (
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Upcoming Tasks Progress</h6>
                  <span className="fw-bold">{stats.upcomingPercentage.toFixed(1)}%</span>
                </div>
                <ProgressBar 
                  now={stats.upcomingPercentage} 
                  variant="success"
                  className="mb-2"
                  style={{ height: '8px' }}
                />
                <small className="text-muted">
                  {stats.upcoming} of {todos.length} tasks are upcoming
                </small>
              </Card.Body>
            </Card>
          )}

          {/* FILTERS & SEARCH */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Body>
              <Row className="g-3">
                <Col md={5}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Search />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search todos by title, description, or assignee..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => setSearchTerm('')}
                      >
                        Clear
                      </Button>
                    )}
                  </InputGroup>
                </Col>
                
                <Col md={3}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Filter />
                    </InputGroup.Text>
                    <Form.Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Tasks</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="overdue">Overdue</option>
                      <option value="notification">With Notification</option>
                    </Form.Select>
                  </InputGroup>
                </Col>
                
                <Col md={2}>
                  <InputGroup>
                    <InputGroup.Text>
                      <SortAlphaDown />
                    </InputGroup.Text>
                    <Form.Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="date">Sort by Date</option>
                      <option value="title">Sort by Title</option>
                      <option value="user">Sort by Assignee</option>
                    </Form.Select>
                  </InputGroup>
                </Col>
                
                <Col md={2}>
                  <Button
                    variant={sortOrder === 'asc' ? 'primary' : 'secondary'}
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="w-100"
                  >
                    {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* TODOS LIST */}
      {filteredTodos.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <ListTask size={64} className="text-muted mb-3" />
            <h4 className="mb-3">No Todos Found</h4>
            <p className="text-muted mb-4">
              {searchTerm || filterStatus !== 'all'
                ? 'No todos match your search criteria'
                : 'No todos have been added to this project yet'}
            </p>
            {searchTerm && (
              <Button 
                variant="outline-primary" 
                onClick={() => setSearchTerm('')}
              >
                Clear Search
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <div className="p-4">
              <div className="row g-4">
                {filteredTodos.map((todo, index) => {
                  const dateStatus = getDateStatus(todo.taskDate);
                  const isOverdue = dateStatus.variant === 'danger';
                  
                  return (
                    <div key={todo._id} className="col-12">
                      <Card className={`border-0 shadow-sm mb-3 ${isOverdue ? 'border-danger border-start border-4' : 'border-start border-4 border-primary'}`}>
                        <Card.Body>
                          <Row>
                            <Col md={8}>
                              <div className="d-flex align-items-start mb-3">
                                <div className="me-3">
                                  <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                                    style={{ width: 50, height: 50 }}>
                                    <span className="fw-bold">{index + 1}</span>
                                  </div>
                                </div>
                                <div className="flex-grow-1">
                                  <h5 className="mb-1">{todo.title}</h5>
                                  {todo.description && (
                                    <p className="text-muted mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                                      {todo.description}
                                    </p>
                                  )}
                                  
                                  {/* Assignee Info */}
                                  <div className="d-flex align-items-center mb-2">
                                    <div className="me-3">
                                      <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                                        style={{ width: 36, height: 36 }}
                                        title={todo.userId?.name}>
                                        <span className="fw-bold small">{getInitials(todo.userId?.name)}</span>
                                      </div>
                                    </div>
                                    <div>
                                      <small className="fw-medium">{todo.userId?.name || 'Unassigned'}</small>
                                      <div className="text-muted small">
                                        <Envelope size={12} className="me-1" />
                                        {todo.userId?.email || 'No email'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Col>
                            
                            <Col md={4}>
                              <div className="d-flex flex-column h-100">
                                {/* Date & Status */}
                                <div className="mb-3">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <small className="text-muted">Due Date:</small>
                                    <Badge bg={dateStatus.variant} className="px-3">
                                      {dateStatus.label}
                                    </Badge>
                                  </div>
                                  <div className="p-2 bg-light rounded">
                                    <div className="fw-bold text-center">
                                      <Calendar className="me-2" />
                                      {todo.taskDate ? formatDate(todo.taskDate) : 'No date'}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Task Info */}
                                <div className="mt-auto">
                                  <div className="d-flex justify-content-between mb-2">
                                    <small className="text-muted">Created:</small>
                                    <small>{todo.createdAt ? formatDate(todo.createdAt) : 'N/A'}</small>
                                  </div>
                                  
                                  <div className="d-flex justify-content-between mb-2">
                                    <small className="text-muted">Task ID:</small>
                                    <small className="text-truncate" style={{ maxWidth: '120px' }}>
                                      {todo._id?.substring(0, 8) || 'No ID'}...
                                    </small>
                                  </div>
                                  
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      {todo.toggleNotification ? (
                                        <Badge bg="success" className="px-2 py-1">
                                          <BellFill size={12} className="me-1" />
                                          Notifications ON
                                        </Badge>
                                      ) : (
                                        <Badge bg="secondary" className="px-2 py-1">
                                          <Bell size={12} className="me-1" />
                                          Notifications OFF
                                        </Badge>
                                      )}
                                    </div>
                                    
                                    <div>
                                      {todo.reminded ? (
                                        <Badge bg="info" className="px-2 py-1">
                                          <CheckCircleFill size={12} className="me-1" />
                                          Reminded
                                        </Badge>
                                      ) : (
                                        <Badge bg="light" text="dark" className="px-2 py-1">
                                          <Clock size={12} className="me-1" />
                                          Pending
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* SUMMARY */}
            <div className="p-3 bg-light border-top">
              <Row>
                <Col md={6}>
                  <div className="small text-muted">
                    Showing {filteredTodos.length} of {todos.length} tasks
                    {searchTerm && ` matching "${searchTerm}"`}
                    {filterStatus !== 'all' && ` (${filterStatus})`}
                  </div>
                </Col>
                <Col md={6} className="text-end">
                  <div className="d-flex justify-content-end align-items-center">
                    <div className="me-3">
                      <small className="text-muted">Sorted by:</small>
                      <Badge bg="info" className="ms-2">
                        {sortBy === 'date' ? 'Date' : sortBy === 'title' ? 'Title' : 'Assignee'} 
                        ({sortOrder === 'asc' ? 'Asc' : 'Desc'})
                      </Badge>
                    </div>
                    <div>
                      <Badge bg="warning" className="me-2">
                        {stats.overdue} Overdue
                      </Badge>
                      <Badge bg="success">
                        {stats.upcoming} Upcoming
                      </Badge>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* NO DATA INFO */}
      {todos.length === 0 && !loading && (
        <Alert variant="info" className="mt-4">
          <div className="d-flex align-items-center">
            <ListTask className="me-3" size={24} />
            <div>
              <h6 className="mb-1">No Todos Available</h6>
              <p className="mb-0">This project doesn't have any todos yet. Todos can be added through the main application interface.</p>
            </div>
          </div>
        </Alert>
      )}
    </Container>
  );
};

export default ToDoList;